import React, { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import {
  type OnCompleteParams,
  allowedImageTypesList,
} from "simplydo/react/components/Choosers/FileChooser/uploadUtils";
import { styled } from "simplydo/react/styled";
import { Button, Divider, Grid, Pagination } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";
import { Modal } from "simplydo/react/ui";

import type { DropZoneConfigProps } from "./FileChooser/DropZone";
import { FileUploaderPopup } from "./FileChooser/FileUploaderPopup";

// @ts-ignore
const clientId = import.meta.env.VITE_UNSPLASH_CLIENT_ID;

type UnsplashImage = {
  urls: { thumb: string };
  alt_description?: string | null;
  links: { download_location: string };
  user: { username: string; name: string };
};

const UnsplashThumb = styled.div`
  width: 100%;
  height: 150px;
  background-size: cover;
  background-position: center center;
  cursor: pointer;
  display: inline-block;
  position: relative;

  p {
    display: none;
    font-size: 13px;
    position: absolute;
    top: 0px;
    left: 0px;
    margin: 2px;
  }

  .unsplash-fader {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    opacity: 0;
    background: linear-gradient(
      rgba(250, 250, 250, 0.9),
      rgba(250, 250, 250, 0)
    );
    transition: opacity 0.25s;
  }

  &:hover {
    .unsplash-fader {
      opacity: 0.8;
    }
    p {
      display: block;
    }
  }
`;

type ImageChooserProps = {
  forType: string;
  forId: string;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onComplete?: (
    responseFilename: string,
    filename: string,
    responseDownload: string,
  ) => void;
  allowMultiple?: boolean;
  onCompleteMultiple?: (files: OnCompleteParams[]) => void;
  trigger?: React.ReactElement;
  content?: string;
  isBasic?: boolean;
  primary?: boolean;
  secondary?: boolean;
  disabled?: boolean;
  dropzoneProps?: DropZoneConfigProps;
};

export const ImageChooser = ({
  forType,
  forId,
  isOpen,
  onOpen,
  onClose,
  onComplete,
  allowMultiple,
  onCompleteMultiple,
  trigger,
  content,
  isBasic,
  primary,
  secondary,
  disabled,
  dropzoneProps,
}: ImageChooserProps) => {
  const { t } = useTranslation();
  const [unsplashTerm, setUnsplashTerm] = useState("");
  const [unsplashLoading, setUnsplashLoading] = useState(false);
  const [unsplashImages, setUnsplashImages] = useState<UnsplashImage[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [maximumPage, setMaximumPage] = useState(1);
  const [noResults, setNoResults] = useState(false);
  const [open, setOpen] = useState(trigger ? false : isOpen);

  if (isOpen !== undefined && isOpen !== open) {
    setOpen(isOpen);
  }

  // Button handlers for manually opening and closing the chooser popup
  const closePopup = useCallback(() => {
    if (open && onClose) {
      onClose();
    }
    setOpen(false);
  }, [open, onClose]);

  const openPopup = useCallback(() => {
    if (open) {
      return closePopup();
    }
    if (!open && onOpen) onOpen();
    setOpen(true);
  }, [open, onOpen, closePopup]);

  const onChosen = (
    responseFilename: string,
    filename: string | null,
    responseDownload: string,
  ) => {
    onComplete?.(responseFilename, filename ?? "", responseDownload);
    closePopup();
  };

  const chooseFromUnsplash = (image: UnsplashImage) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", `${image.links.download_location}&client_id=${clientId}`);
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          onChosen(`unsplash-${data.url}`, null, data.url);
        }
      }
    };
    xhr.send();
  };

  const queryUnsplash = (term: string, page?: number) => {
    if (!term) return;
    setUnsplashLoading(true);
    const pageNumber = page ?? 1;
    const xhr = new XMLHttpRequest();
    xhr.open(
      "GET",
      `https://api.unsplash.com/search/photos/?query=${term}&page=${pageNumber}&per_page=8&client_id=${clientId}`,
    );
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          setUnsplashImages(data.results);
          setMaximumPage(data.total_pages);
          setNoResults(data.results.length === 0);
        }
        setUnsplashLoading(false);
      }
    };
    xhr.send();
  };

  const searchUnsplash = () => {
    queryUnsplash(unsplashTerm, 1);
    setCurrentPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.keyCode === 13) {
      e.preventDefault();
      searchUnsplash();
    }
  };

  const triggerElement = useMemo(() => {
    if (trigger) {
      return React.cloneElement(trigger, {
        onClick: disabled ? () => {} : openPopup,
      } as React.HTMLAttributes<HTMLElement>);
    }

    return (
      <Button
        size="small"
        primary={primary}
        secondary={secondary}
        basic={isBasic}
        icon="image"
        disabled={disabled}
        content={content || t("ideas.details.chooseCover")}
        onClick={openPopup}
      />
    );
  }, [trigger, openPopup, disabled, isBasic, content, t, primary, secondary]);

  return (
    <>
      {triggerElement}
      <Modal open={open}>
        <Modal.Header>{t("ideas.actions.chooseImage")}</Modal.Header>
        <Modal.Content>
          <h3>{t("ideas.actions.chooseImage.info")}</h3>
          <FileUploaderPopup
            forType={forType}
            forId={forId}
            onComplete={onChosen}
            allowMultiple={allowMultiple}
            onCompleteMultiple={onCompleteMultiple}
            onError={toast.error}
            accept={allowedImageTypesList.join(",")}
            dropzoneProps={dropzoneProps}
          />
          <Divider section />
          <div>
            <h3>{t("ideas.actions.chooseImage.search")}</h3>
            <p style={{ margin: 0 }}>
              <small>
                Courtesy of{" "}
                <a
                  href="https://unsplash.com/?utm_source=simply_do&utm_medium=referral"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Unsplash
                </a>
              </small>
            </p>

            <Input
              fluid
              icon="search"
              iconPosition="left"
              placeholder={t("ideas.actions.chooseImage.search2")}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                (e.target as HTMLInputElement).focus();
              }}
              onKeyDown={handleClickForKeyboard(searchUnsplash, {
                keys: ["Enter"],
              })}
              onChange={(_e, data) => setUnsplashTerm(String(data.value ?? ""))}
              action={
                <Button
                  loading={unsplashLoading}
                  disabled={!unsplashTerm}
                  primary
                  content={t("ideas.actions.chooseImage.results")}
                  onClick={searchUnsplash}
                />
              }
            />
            {noResults ? (
              <Grid.Column textAlign="center" style={{ marginTop: 20 }}>
                <span>{t("multiimage.upload.none")}</span>
              </Grid.Column>
            ) : (
              <Grid style={{ marginTop: 20 }}>
                {unsplashImages.map((image) => (
                  <Grid.Column width={4} key={image.urls.thumb}>
                    <UnsplashThumb
                      role="button"
                      tabIndex={0}
                      aria-description={
                        image.alt_description || "Unsplash Image"
                      }
                      aria-label="Choose this image"
                      style={{ backgroundImage: `url(${image.urls.thumb})` }}
                      onClick={() => chooseFromUnsplash(image)}
                      onKeyDown={handleClickForKeyboard(() =>
                        chooseFromUnsplash(image),
                      )}
                    >
                      <div className="unsplash-fader" />
                      <p>
                        Photo by{" "}
                        <a
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          href={`https://unsplash.com/@${image.user.username}?utm_source=simply_do&utm_medium=referral`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {image.user.name}
                        </a>
                      </p>
                    </UnsplashThumb>
                  </Grid.Column>
                ))}
              </Grid>
            )}
            {unsplashImages.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  marginTop: 14,
                  justifyContent: "flex-end",
                }}
              >
                <Pagination
                  totalPages={maximumPage}
                  activePage={currentPage}
                  onPageChange={(_e, { activePage }) => {
                    const page = Number(activePage ?? 1);
                    setCurrentPage(page);
                    queryUnsplash(unsplashTerm, page);
                  }}
                />
              </div>
            ) : null}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button content={t("generic.close")} onClick={closePopup} />
        </Modal.Actions>
      </Modal>
    </>
  );
};
